/**
 * Projectflow — de macro-fasen met per fase de tools en panelen die erbij horen.
 *
 * Bron van de fasen zelf is de backend (workflow-templates → project_flows);
 * dit bestand is de declaratieve UI-kant: iconen, tools per fase en de
 * URL-conventie voor projectcontext.
 *
 * URL-conventie voor tools die projectcontext accepteren:
 *   ?project=<recId>&pnr=<nummer>&pnaam=<naam>&from=flow
 * Tools met supportsContext: false krijgen een ⚑-badge ("nog geen
 * projectkoppeling") — eerlijk zichtbaar in plaats van een knop die stil niets
 * meeneemt. Ze koppelen is losstaand vervolgwerk, één tool per keer.
 */

export const buildToolUrl = (tool, project) => {
  if (!tool.supportsContext || !project) return tool.path;
  const recId = project.airtable_id || project.id;
  const pnr = project.fields?.Projectnummer || '';
  const pnaam = project.fields?.Projectnaam || '';
  // Klantnaam apart mee: meetings/mails matchen op klantnaam in de titel,
  // nooit op projectnummer ('9512' geeft 0 treffers, 'HVC' 33 — gemeten).
  const klantRaw = project.fields?.KlantnaamString;
  const klant = Array.isArray(klantRaw) ? (klantRaw[0] || '') : (klantRaw || '');
  const q = new URLSearchParams({ project: recId || '', pnr, pnaam, klant, from: 'flow', ...(tool.query || {}) });
  return `${tool.path}?${q.toString()}`;
};

export const FLOW_PHASES = {
  opstartfase: {
    icon: 'flag',
    beschrijving: 'Briefing en debriefing — wat wil de klant, wie doet wat.',
    generator: {
      kind: 'debrief', output: 'markdown',
      label: 'Genereer debrief',
      uitleg: 'Vat de briefing samen tot afspraken en open punten',
    },
    tools: [
      { label: 'Meetings', path: '/MeetingDashboard', icon: 'mic', supportsContext: true },
      { label: 'Frits (mail-assistent)', path: '/mail-assistent', icon: 'mail', supportsContext: false },
    ],
    // Openen als dialoog binnen het project (geen aparte pagina) — de
    // taakgeneratie leest de lokale offerteregels van dít project.
    dialogActions: [
      { key: 'smartTasks', label: 'Slimme taken (offerte)', icon: 'auto_awesome' },
      { key: 'preproWizard', label: 'Projecttaken genereren', icon: 'playlist_add' },
    ],
    panels: ['activiteit', 'notes', 'meetings', 'documenten'],
  },
  offertefase: {
    icon: 'request_quote',
    beschrijving: 'Van debriefing naar geaccepteerde offerte in Gripp.',
    // Sales Dashboard hoort hier bewust NIET: management-niveau, geen
    // PL-gereedschap binnen één project (Joris, 17 aug 2026).
    tools: [
      { label: 'Offerte Generator', path: '/offertes', icon: 'request_quote', supportsContext: true },
    ],
    panels: ['offerteregels', 'correspondentie', 'documenten'],
  },
  concept: {
    icon: 'lightbulb',
    beschrijving: 'Van briefing naar goedgekeurd concept: ideeën, moodboard en presentatie.',
    tools: [
      // Duidelijk onderscheid: eigen opgeleverd werk vs. externe referenties
      { label: 'Eigen werk doorzoeken', path: '/video-search', icon: 'search', supportsContext: false },
      { label: 'Referenties zoeken', path: '/inspiration', icon: 'auto_awesome', supportsContext: false },
      { label: 'Explainer Studio', path: '/explainer-script', icon: 'edit_note', supportsContext: false },
      { label: 'Effie Gids', path: '/effie-guide', icon: 'emoji_events', supportsContext: false },
    ],
    generator: {
      kind: 'concept', output: 'pptx',
      label: 'Genereer concept (PowerPoint)',
      uitleg: 'Maakt 2-3 conceptrichtingen als presentatie',
    },
    panels: ['notes', 'meetings', 'documenten', 'script'],
  },
  preproductie: {
    taskTypes: ['Preproductie', 'Script', 'Regie', 'Illustratie'],
    icon: 'edit_note',
    beschrijving: 'Concept, script, casting, locaties en planning.',
    tools: [
      { label: 'Script Breakdown', path: '/script-breakdown', icon: 'segment', supportsContext: false },
      { label: 'Explainer Studio', path: '/explainer-script', icon: 'auto_awesome', supportsContext: false },
      { label: 'Callsheets', path: '/callsheets', icon: 'phone', supportsContext: true, query: { new: '1' } },   // direct nieuwe callsheet voor dit project (Joris, 15 sep)
      { label: 'Draaidag-planner', path: '/draaidag-planner', icon: 'event', supportsContext: false },
    ],
    panels: ['taken', 'deliverables', 'notes', 'documenten', 'script'],
  },
  productie: {
    taskTypes: ['Draaidag'],
    icon: 'videocam',
    beschrijving: 'De draaidagen — crew, materiaal en callsheets.',
    tools: [
      { label: 'Callsheets', path: '/callsheets', icon: 'phone', supportsContext: true, query: { new: '1' } },   // direct nieuwe callsheet voor dit project (Joris, 15 sep)
      { label: 'Materialen reserveren', path: '/MaterialReservation', icon: 'camera_alt', supportsContext: false },
      { label: 'Draaidag-planner', path: '/draaidag-planner', icon: 'event', supportsContext: false },
    ],
    panels: ['draaidagen', 'callsheets', 'quitclaims', 'taken', 'documenten'],
  },
  postproductie: {
    taskTypes: ['Montage', 'Animatie', 'Grading', 'Audio'],
    icon: 'movie_edit',
    beschrijving: 'Montage, animatie, geluid en beeldafwerking tot goedgekeurde oplevering.',
    tools: [
      { label: 'Video Edit QA', path: '/video-qa', icon: 'bug_report', supportsContext: false },
      { label: 'SRT Generator', path: '/SRTGenerator', icon: 'subtitles', supportsContext: false },
      { label: 'Ondertitel Studio', path: '/ritmo', icon: 'music_note', supportsContext: false },
    ],
    panels: ['deliverables', 'frameio', 'qa', 'taken', 'documenten'],
  },
  afronding: {
    taskTypes: ['Projectafronding', 'Archief'],
    icon: 'inventory_2',
    beschrijving: 'Opleveren, factureren en archiveren.',
    tools: [],
    panels: ['offerteregels', 'correspondentie', 'activiteit', 'documenten'],
  },
  evaluatie: {
    icon: 'reviews',
    beschrijving: 'Evaluatie en klanttevredenheid.',
    tools: [
      { label: 'Klanttevredenheid', path: '/enquete-dashboard', icon: 'sentiment_satisfied', supportsContext: false },
    ],
    panels: ['correspondentie', 'documenten'],
  },
};

// Nette labels voor de paneel-drawer (zelfde keys als de TabPanel-keys).
export const PANEL_LABELS = {
  activiteit: 'Activiteit',
  taken: 'Taken',
  deliverables: 'Deliverables',
  draaidagen: 'Draaidagen',
  quitclaims: 'Quitclaims',
  callsheets: 'Callsheets',
  notes: 'Notities',
  meetings: 'Meetings',
  frameio: 'Frame.io',
  qa: 'QA Reviews',
  correspondentie: 'Correspondentie',
  offerteregels: 'Offerteregels',
  documenten: 'Documenten',
  script: 'Script',
};

export const STATUS_STYLES = {
  klaar: { color: '#059669', bg: '#ecfdf5', label: 'Klaar' },
  bezig: { color: '#d97706', bg: '#fef3c7', label: 'Bezig' },
  todo: { color: '#94a3b8', bg: '#f1f5f9', label: 'Nog niet' },
};
